import { merge } from 'lodash-es';
import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';
import { computed, ref, Ref } from 'vue';

import apiGw from '@/services/apiGw';
import { Link } from '@/types/general';
import { useUserStore } from '@/store/userStore';
import { AccountStats, CashAccount } from '@/types/cashAccount';
import { useCardsStore } from '@/store/cardsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { getUidFromSelfLink, getUidsFromUri } from '@/utils/links';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { addYapAmount, SseListener } from '@/utils/general';
import { createResourceLoadingMixin } from '@/store/resourceLoadingMixin';

export const useCashAccountsStore = defineStore('cashAccounts', () => {
  const contextSwitchStore = useContextSwitchStore();
  const userStore = useUserStore();
  const eventStreamStore = useEventStreamStore();
  const resourceLoadingMixin = createResourceLoadingMixin();

  // STATES
  const selectedCashAccountUid: Ref<string | undefined> = ref(undefined);
  const cashAccounts: Ref<{ [key: string]: CashAccount }> = ref({});
  const cashAccountsInContext: Ref<Map<string, Set<string>>> = ref(new Map());
  const accountsStats: Ref<{ [key: string]: AccountStats }> = ref({});
  const pendingPromises: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});
  const pendingDetailsPromises: Ref<{ [key: string]: Promise<AxiosResponse> }> = ref({});

  // GETTERS
  const isLoading = computed(
    () => Object.keys(pendingPromises.value).length > 0 || Object.keys(pendingDetailsPromises.value).length > 0
  );
  function getCashAccount(uid: string) {
    return cashAccounts.value[uid];
  }

  function getPrivateCashAccounts() {
    return Object.values(cashAccounts.value).filter((ca: CashAccount) => ca.accountOwner?.uid === userStore.uid);
  }

  const entitledCashAccountsForCurrentContext = computed(() => {
    const currentContext = contextSwitchStore.getCurrentContext();
    if (!currentContext) return [];
    const userStore = useUserStore();
    const userUid = userStore.uid;

    /**
     * Filter the cashAccount from the given context by entitlements.
     * - In case of private user, there is no context so we return all
     * - In case of SME account, we only return the CashAccounts for which this user has some rights to
     *   as well as the accounts for which he has a card
     */
    const cardsStore = useCardsStore();
    const cards4ctx = cardsStore.getCardsForContext(currentContext.uid).map((c) => c.baseCardDetails.cardUID);

    const filteredCashAccounts = cashAccountsForCurrentContext.value.filter(
      (acc) =>
        !acc.entitlements ||
        acc.entitlements.map((e) => e.user?.uid).includes(userUid) ||
        cards4ctx.includes(getUidFromSelfLink(acc._links.self))
    );
    return filteredCashAccounts;
  });

  const entitledActiveCashAccountsForCurrentContext = computed(() => {
    return entitledCashAccountsForCurrentContext.value.filter((ca) => ca.status === 'active');
  });

  const cashAccountsForCurrentContext = computed(() => {
    const currentContext = contextSwitchStore.getCurrentContext();
    if (!currentContext) return [];

    return getCashAccountsForContext(currentContext.uid);
  });

  const getCurrentCashAccount = computed(() => {
    let cashAccount;
    if (selectedCashAccountUid.value) {
      cashAccount = cashAccounts.value[selectedCashAccountUid.value];
    }
    return cashAccount;
  });
  const getCashAccounts = computed(() => {
    return Object.values(cashAccounts.value) as Array<CashAccount>;
  });

  function getCashAccountsForContext(ctxUid: string) {
    if (!ctxUid) return [];
    return Object.values(cashAccounts.value).filter((acc) =>
      cashAccountsInContext.value.get(ctxUid)?.has(getUidFromSelfLink(acc._links.self))
    );
  }

  function getEntitledCashAccountsForContext(ctxUid: string) {
    const userStore = useUserStore();
    const cas: Array<CashAccount> = Object.values(cashAccounts.value);
    const cashAccounts4ctx = cas.filter((acc) =>
      cashAccountsInContext.value.get(ctxUid)?.has(getUidFromSelfLink(acc._links.self))
    );
    const userUid = userStore.uid;
    /**
     * Filter the cashAccount from the given context by entitlements.
     * - In case of private user, there is no context so we return all
     * - In case of SME account, we only return the CashAccounts for which this user has some rights to
     *   as well as the accounts for which he has a card
     */
    const cardsStore = useCardsStore();
    const cards4ctx = cardsStore.getCardsForContext(ctxUid).map((c) => c.baseCardDetails.cardUID);
    const filteredCashAccounts = cashAccounts4ctx.filter(
      (acc) =>
        !acc.entitlements ||
        acc.entitlements.map((e) => e.user?.uid).includes(userUid) ||
        cards4ctx.includes(getUidFromSelfLink(acc._links.self))
    );
    return filteredCashAccounts;
  }

  const cashAccountsBalanceForCurrentContext = computed(() => {
    const currentContext = contextSwitchStore.getCurrentContext();
    if (!currentContext) {
      return undefined;
    }
    if (!entitledActiveCashAccountsForCurrentContext.value.length) return undefined;

    return entitledActiveCashAccountsForCurrentContext.value.reduce((acc, ca) => {
      return acc + parseFloat(ca.totalValuation || '0');
    }, 0);
  });

  const totalStatisticsForCurrentContext = computed(() => {
    const currentContext = contextSwitchStore.getCurrentContext();
    if (!currentContext || !entitledActiveCashAccountsForCurrentContext.value.length) {
      return undefined;
    }

    return entitledActiveCashAccountsForCurrentContext.value.reduce(
      (acc, ca) => {
        if (!ca.uid || !accountsStats.value[ca.uid]) {
          return acc;
        }

        return {
          debit: {
            amount: addYapAmount(acc.debit.amount, accountsStats.value[ca.uid]?.debit.amount),
            currency: accountsStats.value[ca.uid]?.debit.currency
          },
          credit: {
            amount: addYapAmount(acc.credit.amount, accountsStats.value[ca.uid]?.credit.amount),
            currency: accountsStats.value[ca.uid]?.credit.currency
          },
          saved: {
            amount: addYapAmount(acc.saved.amount, accountsStats.value[ca.uid]?.saved.amount),
            currency: accountsStats.value[ca.uid]?.saved.currency
          },
          total: {
            amount: addYapAmount(acc.total.amount, accountsStats.value[ca.uid]?.total.amount),
            currency: accountsStats.value[ca.uid]?.total.currency
          }
        };
      },
      {
        debit: { amount: { value: 0, factor: 0 }, currency: 'CHF' },
        credit: { amount: { value: 0, factor: 0 }, currency: 'CHF' },
        saved: { amount: { value: 0, factor: 0 }, currency: 'CHF' },
        total: { amount: { value: 0, factor: 0 }, currency: 'CHF' }
      }
    );
  });

  function getCashAccountsBalanceForContext(ctxUid: string) {
    if (!ctxUid) {
      return 'no ctx';
    }
    const cas: Array<CashAccount> = Object.values(cashAccounts.value);
    const accounts = cas.filter(
      (acc) =>
        cashAccountsInContext.value.get(ctxUid)?.has(getUidFromSelfLink(acc._links.self)) && acc.currency === 'CHF'
    );

    if (!accounts.length) {
      return undefined;
    }

    // return the sum of all account totalValuation
    return accounts.reduce((acc, ca) => {
      return acc + parseFloat(ca.totalValuation || '0');
    }, 0);
  }

  function getIbanForCashAccountUid(cashAccountUid: string): string | undefined {
    const cashAccount = cashAccounts.value[cashAccountUid];
    if (cashAccount) {
      return cashAccount.iban;
    }
    return undefined;
  }

  // ACTIONS
  function $reset() {
    selectedCashAccountUid.value = undefined;
    cashAccounts.value = {};
    cashAccountsInContext.value.clear();
    pendingPromises.value = {};
    pendingDetailsPromises.value = {};
    resourceLoadingMixin.reset();
  }

  function selectCashAccount(uid?: string) {
    if (uid !== undefined) {
      /**
       * Check if the user is entitled to view the cash account and only set the selectedCashAccountUid if he is
       * Setting it if the user is not entitled can cause auto-logout if the BE returns a 401 (which it currently does)
       */
      const entitledCashAccountUids = entitledCashAccountsForCurrentContext.value.map((ca) =>
        getUidFromSelfLink(ca._links.self)
      );
      if (!entitledCashAccountUids.includes(uid)) {
        return;
      }
    }
    selectedCashAccountUid.value = uid;
  }

  async function loadCashAccounts(uri: string, forceReload = false) {
    const ctxUid = getUidsFromUri(uri)[0];
    if (!forceReload && cashAccountsInContext.value.has(ctxUid)) {
      return cashAccountsInContext.value.get(ctxUid);
    }

    const promise = pendingPromises.value[uri];
    if (!forceReload && !!promise) {
      await pendingPromises.value[uri];
    } else {
      const request = apiGw.get(uri);
      pendingPromises.value[uri] = request;

      const response: AxiosResponse = await request;
      const cashAccs: Array<CashAccount> = response.data;
      const type = SseListener.CashSubAccount;

      const staleUidOfType = eventStreamStore.staleUids.get(type);
      if (cashAccs.length) {
        cashAccs.map((ca) => {
          cashAccounts.value[getUidFromSelfLink(ca._links.self)] = ca;
        });
        const casInContext = cashAccountsInContext.value.get(ctxUid) || new Set();
        cashAccs.forEach((cashAccount: CashAccount) => {
          const uid = getUidFromSelfLink(cashAccount._links.self);

          casInContext.add(uid);

          if (staleUidOfType && staleUidOfType[uid]) {
            eventStreamStore.unstaleUid(uid, type);
          }
        });
        cashAccountsInContext.value.set(ctxUid, casInContext);
      }
      delete pendingPromises.value[uri];
    }

    return cashAccountsInContext.value.get(ctxUid);
  }

  async function getCashAccountDetails(link: Link): Promise<CashAccount | undefined> {
    if (!link) {
      return;
    }
    const cashAccountUid = getUidFromSelfLink(link);
    const ca = cashAccounts.value[cashAccountUid];
    if (ca?._actions) {
      return ca;
    }
    const promise = pendingDetailsPromises.value[cashAccountUid];
    if (promise) {
      await promise;
    } else {
      const request = apiGw.get(link.uri);
      pendingDetailsPromises.value[cashAccountUid] = request;
      const response = await request;
      cashAccounts.value[cashAccountUid] = merge(cashAccounts.value[cashAccountUid], response.data);
      delete pendingDetailsPromises.value[cashAccountUid];
    }
    return cashAccounts.value[cashAccountUid];
  }

  function updateCashAccountTotalValuation(cashAccountUid: string, totalValuation: string) {
    const cashAccount = cashAccounts.value[cashAccountUid];
    if (cashAccount) {
      cashAccount.totalValuation = totalValuation;
    }
  }

  async function patchCashAccountName(cashAccountUid: string, newName: string) {
    const cashAccount = cashAccounts.value[cashAccountUid];
    if (cashAccount) {
      cashAccount.accountName = newName;
    }
  }

  async function loadStatsForAccountUid(uid: string, options: { forceReload?: boolean } = {}): Promise<AccountStats> {
    const accountStatsUri = `/v2/accounts/${uid}/stats`;
    if (options.forceReload) {
      resourceLoadingMixin.removeResource(accountStatsUri);
    }

    return resourceLoadingMixin.loadResource(accountStatsUri, async () => {
      const response = await apiGw.get(accountStatsUri);
      accountsStats.value[uid] = response.data;
      return response.data;
    });
  }

  return {
    // STATES
    selectedCashAccountUid,
    cashAccounts,
    cashAccountsInContext,
    accountsStats,
    pendingPromises,
    pendingDetailsPromises,
    totalStatisticsForCurrentContext,
    // GETTERS
    isLoading,
    getCashAccount,
    getPrivateCashAccounts,
    cashAccountsForCurrentContext,
    entitledCashAccountsForCurrentContext,
    entitledActiveCashAccountsForCurrentContext,
    getCurrentCashAccount,
    getCashAccounts,
    getCashAccountsForContext,
    getEntitledCashAccountsForContext,
    cashAccountsBalanceForCurrentContext,
    getCashAccountsBalanceForContext,
    getIbanForCashAccountUid,
    // ACTIONS
    $reset,
    selectCashAccount,
    loadCashAccounts,
    getCashAccountDetails,
    updateCashAccountTotalValuation,
    patchCashAccountName,
    loadStatsForAccountUid
  };
});
